import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';
import { partialEditor } from 'domains/reporter/RichTextEditor/utils';
import { logger } from 'modules/logger';
import { unfurlMacroPlaceholderStatic, usePlugins } from 'domains/reporter/RichTextEditor';
import { useCallback, useEffect } from 'react';
import useAutoloadTemplate from './useAutoloadTemplate';
import type { ReportTemplateFieldsFragment, Json } from 'generated/graphql';
import {
  Editor,
  type PointRef,
  Transforms,
  useSlateStatic,
} from '../domains/reporter/RichTextEditor/core';
import { hydratePlaceholders } from '../domains/reporter/RichTextEditor/plugins/placeholder/utils';
import { useCurrentWorklistItems } from './useCurrentWorklistItems';
import { useCurrentCaseReport } from './useCurrentCaseReport';
import { useCurrentUser } from './useCurrentUser';
import type { GroupedWorklistItems } from './useCurrentWorklistItems';
import { usePicklistStore } from '../domains/reporter/Template/usePicklist';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import { useRequiredFieldIndicator } from './useRequiredFieldIndicator';
import { useImpressionGenerator } from './useImpressionGenerator';
import { FF } from 'modules/feature-flags';
import { useFeatureFlagEnabled } from '../modules/feature-flags/useFeatureFlagEnabled';
import type { Macros } from '../domains/reporter/Template/usePicklist';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  insertedTemplateMetricsState,
  reportTemplateState,
  templateInsertionCompletionState,
} from 'domains/reporter/Reporter/state';
import type { AutoloadTemplateState } from './useAutoloadTemplate';
import {
  type TemplateInsertionContext,
  type TemplateInsertionStart,
  templateInsertionMetrics,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { registerInsertionPaint } from 'domains/reporter/RichTextEditor/analytics/insertionPaint';
import { performDefaultSelection } from './useRichTextEditorDefaultSelection';
import { useRecorder } from 'common/Recorder/useRecorder';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState';
import { WithApolloContext } from 'modules/Apollo/types';

type UseReportTemplateReturn = {
  insertTemplate: (
    templateToInsert: ReportTemplateFieldsFragment,
    insertionContext?: TemplateInsertionContext
  ) => boolean;
  template: Nullable<ReportTemplateFieldsFragment>;
  setTemplate: (arg1?: Nullable<ReportTemplateFieldsFragment>) => void;
  unfurlTemplate: (arg1: ReportTemplateFieldsFragment) => void;
  autoloadTemplate: (insertionContext?: TemplateInsertionStart) => void;
  autoloadTemplateState: AutoloadTemplateState;
};

export type InsertTemplateParams = {
  editor: Editor;
  currentWorklistItems: GroupedWorklistItems;
  ignoreMergeFieldsInDefaultSelection: boolean;
  templateContentToInsert: Json;
};

/**
 * A live handle on the end of the content an insertion just wrote, for the screen-update
 * probe to decorate. Returns undefined when the document has no resolvable end point, in
 * which case the insertion goes unmeasured rather than throwing on a metrics concern.
 */
function anchorInsertedContent(editor: Editor): PointRef | undefined {
  try {
    return Editor.pointRef(editor, Editor.end(editor, []));
  } catch (error) {
    logger.info(
      '[useReportTemplate] - Could not anchor the insertion probe; skipping screen update timing.',
      partialEditor(editor)
    );
    return undefined;
  }
}

// Note that this will move the selection twice. Once, to 0, and then to
// the next heading section bracket. The first will make sure that any
// necessary normalization based on the selection after the withoutNormalizing
// happens at 0. The second places the user's selection where they expect it.
export const insertTemplate = ({
  editor,
  currentWorklistItems,
  ignoreMergeFieldsInDefaultSelection,
  templateContentToInsert,
}: InsertTemplateParams): PointRef | undefined => {
  if (editor == null || editor.children.length === 0) {
    return undefined;
  }

  const editorRange = { anchor: Editor.start(editor, []), focus: Editor.end(editor, []) } as const;
  Editor.withoutNormalizing(editor, () => {
    Transforms.removeNodes(editor, { at: editorRange });
    Transforms.insertNodes(editor, templateContentToInsert, {
      at: [0],
    });
  });

  // Taken after withoutNormalizing exits, so the document is normalized and has a
  // resolvable end point. Slate transforms this ref against every later operation, so it
  // survives the placeholder hydration and default selection that follow.
  const insertedContentRef = anchorInsertedContent(editor);

  hydratePlaceholders(editor, currentWorklistItems);

  // the requestAnimationFrame allows other defaultSelection calls from mounting to finish and allow correct selection resulting from template insertion to be used
  requestAnimationFrame(() => {
    // Ensure consumers observe template insertion even if this runs before Slate's
    // internal onChange bridge is fully attached during initial mount.
    editor.onChange();

    try {
      performDefaultSelection(editor, { ignoreMergeFields: ignoreMergeFieldsInDefaultSelection });
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error(
        '[useReportTemplate] Failed to perform default selection after template insertion',
        { editor: partialEditor(editor), ignoreMergeFieldsInDefaultSelection },
        normalizedError
      );
    }
  });

  return insertedContentRef;
};

export const useReportTemplate = ({ context }: WithApolloContext): UseReportTemplateReturn => {
  const editor = useSlateStatic();
  const pluginsBag = usePlugins();

  const { setMacros } = usePicklistStore();

  const [template, setTemplate] = useRecoilState(reportTemplateState);
  const { currentWorklistItems, loading: currentWorklistItemsLoading } = useCurrentWorklistItems();
  const { data } = useCurrentUser({ context });
  const me = data?.me;
  const ignoreMergeFieldsInDefaultSelection =
    me?.reporterSettings.mergeFieldsSettings?.ignoreDefaultSelection ?? false;
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const { resetRequiredFields } = useRequiredFieldIndicator();
  const { resetGenerateImpressionState, setIsFirstNavigationToImpressionSection } =
    useImpressionGenerator();
  const { currentCaseReport } = useCurrentCaseReport();
  const { isRecording, restartRecording } = useRecorder();
  const [insertedTemplateMetricsValue, setInsertedTemplateMetricsState] = useRecoilState(
    insertedTemplateMetricsState
  );
  const setTemplateInsertionCompletionState = useSetRecoilState(templateInsertionCompletionState);

  const unfurlTemplate = useCallback(
    (template: ReportTemplateFieldsFragment) => {
      if (pluginsBag == null || pluginsBag.getEditorStateEnhancers() == null) {
        const unfurlTemplateError =
          '[ReporterComponent] No Slate editor state enhancers found. Maybe the editor has not fully initialized. Please try again.';
        logger.error(unfurlTemplateError, {
          logId: editor?.selection != null ? stringifyRange(editor.selection) : 'null',
          editor: editor != null ? partialEditor(editor) : 'null',
          template: {
            name: template.name,
            smid: template.id,
          },
        });
        return;
      }

      const newTemplate = {
        ...template,
        sections: unfurlMacroPlaceholderStatic(
          template.sections,
          template.macros,
          pluginsBag.getEditorStateEnhancers()
        ),
        // in the unified editor world, templates have 'content' but may not apply to old templates
        ...(template.content != null && {
          content: unfurlMacroPlaceholderStatic(
            template.content,
            template.macros,
            pluginsBag.getEditorStateEnhancers()
          ),
        }),
      } as const;

      setMacros((stateMacros: Macros): Macros => {
        const oldMacros = stateMacros ?? [];
        const newMacros = (newTemplate.macros ?? []).map((m) => ({ ...m, nestedMacros: [] }));

        return uniqBy(prop('smid'), [...oldMacros, ...newMacros]);
      });
      setTemplate(newTemplate);
    },
    [editor, pluginsBag, setMacros, setTemplate]
  );
  const { autoloadTemplate, autoloadTemplateState } = useAutoloadTemplate({
    unfurlTemplate,
    context,
  });
  const { isAddendumStatus } = useReportLockedState({ context });

  useEffect(() => {
    if (currentCaseReport == null || currentCaseReport.report == null || isAddendumStatus) {
      return;
    }

    const currentCaseReportTemplate = currentCaseReport.report.template;
    // template is the recoilState, our source of truth of the current template
    // currentCaseReportTemplate is the template from the current case report
    if (template == null && currentCaseReportTemplate != null) {
      unfurlTemplate(currentCaseReportTemplate as ReportTemplateFieldsFragment);
    }
  }, [template, currentCaseReport, unfurlTemplate, isAddendumStatus]);

  const insertTemplateCallback = useCallback(
    (
      templateToInsert: ReportTemplateFieldsFragment,
      insertionContext?: TemplateInsertionContext
    ): boolean => {
      if (editor == null || editor.children.length === 0) {
        if (insertionContext != null) templateInsertionMetrics.cancel(insertionContext);
        logger.info(
          '[useReportTemplate] - Editor is null or empty, will not insert template.',
          editor != null ? partialEditor(editor) : null
        );
        return false;
      }
      // Sometimes the editor structure is not ready or valid, so this is a safety check before inserting the template since it
      // throw an error on the Editor.start/end calls in insertTemplate
      try {
        Editor.start(editor, []);
        Editor.end(editor, []);
      } catch (error) {
        if (insertionContext != null) templateInsertionMetrics.cancel(insertionContext);
        logger.info(
          '[useReportTemplate] - Editor structure is invalid, cannot get start/end points',
          partialEditor(editor)
        );
        return false;
      }
      resetRequiredFields();

      let cancelInsertionPaint: (() => void) | undefined;
      let didInsertTemplate = false;
      try {
        const insertedContentRef = insertTemplate({
          editor,
          currentWorklistItems,
          ignoreMergeFieldsInDefaultSelection,
          templateContentToInsert: templateToInsert.content,
        });
        didInsertTemplate = true;

        if (insertionContext == null) {
          insertedContentRef?.unref();
        } else if (insertedContentRef != null) {
          // Registering after the transform, because the decoration needs a real path in
          // the live document rather than one in a detached fragment. Still the same
          // synchronous task, so the render this insertion causes picks it up.
          cancelInsertionPaint = registerInsertionPaint({
            editor,
            insertionId: insertionContext.insertionId,
            anchorRef: insertedContentRef,
            onPainted: () => templateInsertionMetrics.markScreenUpdate(insertionContext),
            onCanceled: () => templateInsertionMetrics.cancel(insertionContext),
          });
          templateInsertionMetrics.markEditDocument(insertionContext);
        } else {
          templateInsertionMetrics.markEditDocument(insertionContext);
          templateInsertionMetrics.cancel(insertionContext);
        }
      } catch (error) {
        cancelInsertionPaint?.();
        if (insertionContext != null) templateInsertionMetrics.cancel(insertionContext);
        throw error;
      }

      setTemplateInsertionCompletionState((previousState) => ({
        sequence: previousState.sequence + 1,
        templateId: templateToInsert.id,
        source:
          insertedTemplateMetricsValue?.templateId === templateToInsert.id
            ? insertedTemplateMetricsValue.source
            : (insertedTemplateMetricsValue?.source ?? previousState.source),
      }));

      if (isAutoImpressionsEnabled) {
        resetGenerateImpressionState();
        setIsFirstNavigationToImpressionSection(true);
      }

      if (isRecording) {
        restartRecording();
      }

      return didInsertTemplate;
    },
    [
      editor,
      resetRequiredFields,
      currentWorklistItems,
      ignoreMergeFieldsInDefaultSelection,
      setTemplateInsertionCompletionState,
      insertedTemplateMetricsValue,
      isAutoImpressionsEnabled,
      isRecording,
      resetGenerateImpressionState,
      setIsFirstNavigationToImpressionSection,
      restartRecording,
    ]
  );

  return {
    insertTemplate: insertTemplateCallback,
    template,
    unfurlTemplate,
    setTemplate,
    autoloadTemplate,
    autoloadTemplateState,
  };
};

export const useReportTemplateValue = (): Nullable<ReportTemplateFieldsFragment> =>
  useRecoilValue(reportTemplateState);
